import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { MapPin, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { SectionLabel } from '@/components/SectionLabel';
import { ApiError } from '@/lib/api/client';
import {
  useCreateSite,
  useCreateWell,
  useDeleteSite,
  useRenameSite,
  useSites,
  useWells,
} from '../api';
import type { IssuedWellToken, Site, SiteWithWellCount, WellWithStatus } from '../types';
import { NameFormDialog } from './NameFormDialog';
import { WellDetailDialog } from './WellDetailDialog';
import { TokenShowOnceDialog } from './TokenShowOnceDialog';

function ago(iso: string | null): string {
  return iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : 'never';
}

function toastError(err: unknown, fallback: string) {
  toast.error(err instanceof ApiError ? err.message : fallback);
}

// Wells for a single selected site.
function WellsPanel({
  site,
  customerId,
  onOpenWell,
}: {
  site: SiteWithWellCount;
  customerId: string;
  onOpenWell: (wellId: string) => void;
}) {
  const { data: wells, isLoading, isError, error } = useWells(site.id);
  const createWell = useCreateWell(site.id, customerId);
  const [addOpen, setAddOpen] = useState(false);

  const columns: DataTableColumn<WellWithStatus>[] = [
    {
      id: 'name',
      header: 'Well',
      cell: (w) => (
        <div>
          <div className="font-medium">{w.name}</div>
          <code className="text-xs text-muted-foreground">{w.wellCode}</code>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (w) =>
        w.isActive ? (
          <StatusBadge variant="success" label="Active" />
        ) : (
          <StatusBadge variant="neutral" label="Inactive" />
        ),
    },
    {
      id: 'token',
      header: 'Token',
      cell: (w) =>
        w.activeToken ? (
          <StatusBadge variant="info" label={`${w.activeToken.tokenPrefix}…`} />
        ) : (
          <span className="text-xs text-muted-foreground">none</span>
        ),
    },
    {
      id: 'last',
      header: 'Last reading',
      cell: (w) => <span className="text-xs text-muted-foreground">{ago(w.lastReadingAt)}</span>,
    },
  ];

  return (
    <div className="space-y-3" data-testid="wells-panel">
      <div className="flex items-center justify-between">
        <SectionLabel>Wells in {site.name}</SectionLabel>
        <Button
          size="sm"
          variant="outline"
          data-testid="add-well-open"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add well
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={wells}
        getRowKey={(w) => w.id}
        isLoading={isLoading}
        isError={isError}
        errorState={<div className="text-center text-sm text-destructive">{error?.message}</div>}
        emptyState={
          <EmptyState title="No wells yet" description="Add the first well to this site." />
        }
        rowProps={(w) => ({
          className: 'cursor-pointer',
          onClick: () => onOpenWell(w.id),
          'data-testid': `well-row-${w.id}`,
        })}
      />

      <NameFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add well"
        description="A unique well code is generated automatically and shown after creation."
        label="Well name"
        submitLabel="Create well"
        isPending={createWell.isPending}
        testId="add-well-dialog"
        onSubmit={async (name) => {
          try {
            await createWell.mutateAsync({ name });
            toast.success('Well created');
          } catch (err) {
            toastError(err, 'Failed to create well');
            throw err;
          }
        }}
      />
    </div>
  );
}

export function SitesAndWells({ customerId }: { customerId: string }) {
  const { data: sites, isLoading, isError, error } = useSites(customerId);
  const createSite = useCreateSite(customerId);
  const renameSite = useRenameSite(customerId);
  const deleteSite = useDeleteSite(customerId);

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Site | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SiteWithWellCount | null>(null);
  const [detailWellId, setDetailWellId] = useState<string | null>(null);
  const [issuedToken, setIssuedToken] = useState<IssuedWellToken | null>(null);

  // Keep a valid selection: pick the first site once loaded, or when the
  // current selection disappears (e.g. after a delete).
  useEffect(() => {
    if (sites && sites.length > 0 && !sites.some((s) => s.id === selectedSiteId)) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  const selectedSite = sites?.find((s) => s.id === selectedSiteId) ?? null;

  const siteColumns: DataTableColumn<SiteWithWellCount>[] = [
    { id: 'name', header: 'Site', cell: (s) => <span className="font-medium">{s.name}</span> },
    { id: 'wells', header: 'Wells', cell: (s) => s.wellCount },
    {
      id: 'actions',
      header: '',
      className: 'text-right',
      cell: (s) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            data-testid={`site-rename-${s.id}`}
            onClick={(e) => {
              e.stopPropagation();
              setRenameTarget(s);
            }}
          >
            Rename
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid={`site-delete-${s.id}`}
            // Wells are retained indefinitely and can't be removed, so a site
            // with wells can never be deleted — disable with an explanation.
            disabled={s.wellCount > 0}
            title={
              s.wellCount > 0
                ? 'Sites with wells cannot be deleted. Wells are retained; deactivate them instead.'
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(s);
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSite.mutateAsync(deleteTarget.id);
      toast.success('Site deleted');
      setDeleteTarget(null);
    } catch (err) {
      toastError(err, 'Failed to delete site');
    }
  };

  const noSites = !isLoading && !isError && (sites?.length ?? 0) === 0;

  return (
    <div className="space-y-6" data-testid="sites-and-wells">
      <div className="flex items-center justify-between">
        <SectionLabel>Sites</SectionLabel>
        <Button size="sm" data-testid="add-site-open" onClick={() => setAddSiteOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add site
        </Button>
      </div>

      {noSites ? (
        <EmptyState
          Icon={MapPin}
          title="No sites yet"
          description="Create a site, then add wells and issue device tokens."
        />
      ) : (
        <DataTable
          columns={siteColumns}
          rows={sites}
          getRowKey={(s) => s.id}
          isLoading={isLoading}
          isError={isError}
          errorState={<div className="text-center text-sm text-destructive">{error?.message}</div>}
          rowProps={(s) => ({
            className: s.id === selectedSiteId ? 'cursor-pointer bg-muted/50' : 'cursor-pointer',
            onClick: () => setSelectedSiteId(s.id),
            'data-testid': `site-row-${s.id}`,
          })}
        />
      )}

      {selectedSite && (
        <WellsPanel site={selectedSite} customerId={customerId} onOpenWell={setDetailWellId} />
      )}

      {/* Add site */}
      <NameFormDialog
        open={addSiteOpen}
        onOpenChange={setAddSiteOpen}
        title="Add site"
        label="Site name"
        submitLabel="Create site"
        isPending={createSite.isPending}
        testId="add-site-dialog"
        onSubmit={async (name) => {
          try {
            await createSite.mutateAsync({ name });
            toast.success('Site created');
          } catch (err) {
            toastError(err, 'Failed to create site');
            throw err;
          }
        }}
      />

      {/* Rename site */}
      <NameFormDialog
        open={renameTarget !== null}
        onOpenChange={(open) => !open && setRenameTarget(null)}
        title="Rename site"
        label="Site name"
        submitLabel="Save"
        initialValue={renameTarget?.name ?? ''}
        isPending={renameSite.isPending}
        testId="rename-site-dialog"
        onSubmit={async (name) => {
          if (!renameTarget) return;
          try {
            await renameSite.mutateAsync({ id: renameTarget.id, name });
            toast.success('Site renamed');
          } catch (err) {
            toastError(err, 'Failed to rename site');
            throw err;
          }
        }}
      />

      {/* Delete site confirmation */}
      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent data-testid="site-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete site?</DialogTitle>
            <DialogDescription>
              {deleteTarget ? `"${deleteTarget.name}" will be permanently removed.` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              data-testid="site-delete-confirm"
              disabled={deleteSite.isPending}
              onClick={() => void confirmDelete()}
            >
              {deleteSite.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Well detail + token panel */}
      <WellDetailDialog
        wellId={detailWellId}
        siteId={selectedSiteId ?? ''}
        onClose={() => setDetailWellId(null)}
        onIssued={(issued) => {
          // Close the detail dialog first so the show-once dialog isn't stacked
          // on top of it — two open Radix dialogs render broken.
          setDetailWellId(null);
          setIssuedToken(issued);
        }}
      />

      {/* Show-once token — cleared from state on close so it never persists. */}
      <TokenShowOnceDialog issued={issuedToken} onClose={() => setIssuedToken(null)} />
    </div>
  );
}
